import React, { useState } from "react";
import { Typography, Box, Tabs, Tab, Chip } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import ArticleIcon from "@mui/icons-material/Article";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { FAQList } from "../../../../assets/configs/FAQsPage/faqsJson";
import FAQsSection from "../../../CommonComponents/FAQsSection/FAQsSection";
import "./HelpPanel.scss";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`help-tabpanel-${index}`}
            aria-labelledby={`help-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
};

const FAQContent = () => {
    const sellerFAQs = FAQList.sellerFAQs;

    return (
        <Box className="faq-content">
            <Typography variant="h6" className="faq-section-title" sx={{ mb: 3, fontWeight: 600 }}>
                {sellerFAQs.categoryTitle}
            </Typography>
            <FAQsSection
                faqSection={sellerFAQs}
                questionFocusBackgroundColor="#f0f0f0"
                questionNonFocusBackgroundColor="#f0f0f0"
                answerBackgroundColor="#ffffff"
            />
        </Box>
    );
};

const ResourcesContent = () => {
    const resources = [
        {
            title: "Getting Started Guide",
            description: "Learn how to create an attractive profile and start getting orders",
            icon: <ArticleIcon />,
            link: "#",
        },
        {
            title: "Pricing Strategies",
            description: "Tips on setting competitive prices for your services",
            icon: <ArticleIcon />,
            link: "#",
        },
        {
            title: "Portfolio Best Practices",
            description: "How to showcase your work effectively to attract clients",
            icon: <ArticleIcon />,
            link: "#",
        },
        {
            title: "Communication Guidelines",
            description: "Best practices for communicating with clients professionally",
            icon: <ArticleIcon />,
            link: "#",
        },
    ];

    return (
        <Box className="resources-content">
            <Typography variant="h6" className="resources-title" sx={{ mb: 3, fontWeight: 600 }}>
                Helpful Resources
            </Typography>
            <div className="resources-grid">
                {resources.map((resource, index) => (
                    <Box key={index} className="resource-card">
                        <Chip
                            icon={<AutoAwesomeIcon />}
                            label="Coming Soon"
                            size="small"
                            className="coming-soon-chip"
                            sx={{
                                position: "absolute",
                                top: "12px",
                                right: "12px",
                                backgroundColor: "#fff3cd",
                                color: "#856404",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                "& .MuiChip-icon": {
                                    color: "#856404",
                                },
                            }}
                        />
                        <div className="resource-icon">{resource.icon}</div>
                        <Typography variant="h6" className="resource-title" sx={{ fontWeight: 600, mb: 1 }}>
                            {resource.title}
                        </Typography>
                        <Typography variant="body2" className="resource-description" sx={{ color: "#666" }}>
                            {resource.description}
                        </Typography>
                    </Box>
                ))}
            </div>
        </Box>
    );
};

const ContactContent = () => {
    return (
        <Box className="contact-content">
            <Typography variant="h6" className="contact-title" sx={{ mb: 3, fontWeight: 600 }}>
                Get in Touch
            </Typography>
            <div className="contact-info">
                <Box className="contact-card">
                    <div className="contact-icon">
                        <PhoneIcon />
                    </div>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        Contact
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
                        Call us for immediate assistance during business hours
                    </Typography>
                    <Typography
                        component="a"
                        href="tel:+918368857097"
                        sx={{
                            color: "#0066b2",
                            textDecoration: "none",
                            fontWeight: 500,
                            "&:hover": {
                                textDecoration: "underline",
                            },
                        }}
                    >
                        +91-8368857097
                    </Typography>
                </Box>

                <Box className="contact-card">
                    <div className="contact-icon">
                        <EmailIcon />
                    </div>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        Email
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
                        Send us an email and we'll get back to you within 24 hours
                    </Typography>
                    <Typography
                        component="a"
                        href="mailto:info@geniegig.com"
                        sx={{
                            color: "#0066b2",
                            textDecoration: "none",
                            fontWeight: 500,
                            "&:hover": {
                                textDecoration: "underline",
                            },
                        }}
                    >
                        info@geniegig.com
                    </Typography>
                </Box>

                <Box className="contact-card">
                    <div className="contact-icon">
                        <BusinessIcon />
                    </div>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        Address
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
                        Visit us at our office location
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#1a1a1a", lineHeight: 1.6 }}>
                        E-322, Tagore Garden Extension,<br />
                        New Delhi, Delhi 110027,<br />
                        India
                    </Typography>
                </Box>
            </div>

            <Box className="support-hours">
                <Typography variant="body2" sx={{ color: "#666", mt: 3 }}>
                    <strong>Support Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM IST
                </Typography>
            </Box>
        </Box>
    );
};

export const HelpPanel = () => {
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    return (
        <section className="help-panel">
            <Box className="help-panel-content">
                <div className="help-panel-header">
                    <div className="header-icon">
                        <HelpOutlineIcon />
                    </div>
                    <Typography variant="h5" className="help-panel-title">
                        Help & Support
                    </Typography>
                    <Typography variant="body2" className="help-panel-description">
                        Find answers to common questions, explore helpful resources, or contact our support team.
                    </Typography>
                </div>

                <Box className="help-tabs-container">
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        className="help-tabs"
                        sx={{
                            borderBottom: "1px solid #e5e5e5",
                            "& .MuiTab-root": {
                                textTransform: "none",
                                fontWeight: 500,
                                fontSize: "0.875rem",
                                minHeight: "48px",
                                "&.Mui-selected": {
                                    color: "#0066b2",
                                    fontWeight: 600,
                                },
                            },
                            "& .MuiTabs-indicator": {
                                backgroundColor: "#0066b2",
                                height: "3px",
                            },
                        }}
                    >
                        <Tab
                            icon={<HelpOutlineIcon />}
                            iconPosition="start"
                            label="FAQs"
                            id="help-tab-0"
                            aria-controls="help-tabpanel-0"
                        />
                        <Tab
                            icon={<ArticleIcon />}
                            iconPosition="start"
                            label="Resources"
                            id="help-tab-1"
                            aria-controls="help-tabpanel-1"
                        />
                        <Tab
                            icon={<ContactSupportIcon />}
                            iconPosition="start"
                            label="Contact Us"
                            id="help-tab-2"
                            aria-controls="help-tabpanel-2"
                        />
                    </Tabs>
                </Box>

                <div className="help-tabs-content">
                    <TabPanel value={tabValue} index={0}>
                        <FAQContent />
                    </TabPanel>
                    <TabPanel value={tabValue} index={1}>
                        <ResourcesContent />
                    </TabPanel>
                    <TabPanel value={tabValue} index={2}>
                        <ContactContent />
                    </TabPanel>
                </div>
            </Box>
        </section>
    );
};

